import { TIMESLOT_EPOCH_UNIX, type TokenDecoded } from '@pink/shared';

import type { Locale } from '../i18n-module/locale.js';
import type { AppointmentInfo } from './appointment-info.interface.js';
import { APPOINTMENT_TZ, TIMESLOT_TO_MS_FACTOR } from './token.constants.js';

/**
 * Datums-/Uhrzeit-Anzeige für die DACH-Pilotphase: voller Wochentag, voller
 * Monatsname, 2-stellige Stunde/Minute im 24-h-Format, immer `Europe/Berlin` —
 * DST-Wechsel (CEST/CET) berücksichtigt `Intl.DateTimeFormat` selbst.
 *
 * `hourCycle: 'h23'` ist explizit gesetzt, weil die EN-Locale sonst auf
 * 12-h mit AM/PM zurückfällt. Bewusst nicht `hour12: false` — ältere V8-
 * Versionen mappten das auf `h24` und zeigten Mitternacht als „24:05“.
 */
const APPOINTMENT_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: APPOINTMENT_TZ,
};

/**
 * Wandelt einen dekodierten Token in anzeigefertige Termininfo um.
 *
 * Die UI-View bekommt nur fertige Strings — keine `Date`-Objekte, kein
 * BigInt, kein `Intl`-Kontext. Locale-Switching nach Token-Parsing
 * funktioniert, weil der Aufruf parametrisiert ist (kein Caching).
 *
 * @param decoded   Ergebnis von `decodeToken` aus `@pink/shared`.
 * @param locale    Aktive UI-Locale (DE/EN).
 * @param locations Optionales Mapping `locationId → Anzeigename` aus der
 *                  Tenant-Config. Wenn nicht gesetzt oder ohne passenden
 *                  Eintrag, bleibt `locationName` undefiniert; die UI zeigt dann
 *                  gar keine Standort-Zeile (statt einer verwirrenden ID).
 */
export function formatAppointment(
  decoded: TokenDecoded,
  locale: Locale,
  locations?: Readonly<Record<string, string>>,
): AppointmentInfo {
  const epochMs = (TIMESLOT_EPOCH_UNIX + decoded.timeSlotSecondsFromEpoch) * TIMESLOT_TO_MS_FACTOR;
  const formatter = new Intl.DateTimeFormat(locale, APPOINTMENT_FORMAT_OPTIONS);
  const appointmentDate = new Date(epochMs);
  const locationKey = decoded.locationId.toString();
  const locationName = locations?.[locationKey];
  return {
    formattedDateTime: formatter.format(appointmentDate),
    appointmentId: decoded.appointmentId.toString(),
    location: locationKey,
    ...(locationName !== undefined ? { locationName } : {}),
    timeSlot: appointmentDate.toISOString(),
  };
}
